// Onboarding — e-mail + password sign-in [→ social handles → report cadence].
//
// Accounts are created by the owner in the Firebase Console (Authentication
// → Users → Add user, e-mail/password). There is no self-service sign-up,
// no SMS and no in-app password reset. The wizard is therefore a single
// `signin` step; language is picked in Settings (which PATCHes
// /api/me/language).
//
// Which steps run is decided by the service registry (`@/services`,
// enabledOnboardingSteps): `signin` always; `socials` and `reports` only
// while the monitoring service is on, because they talk to /api/me/handles,
// which transcriber-api answers with 501. Those step bodies stay in this
// file so they come back with one registry change.
//
// After sign-in we wait for the auth module's /api/auth/session round-trip
// (`useAuthSession().status === 'ready'`), refresh /api/me, and hand off:
// a `pending` account → AppShell shows the PendingApprovalScreen; anything
// else → the next enabled step, or onFinish (default route).

import { useEffect, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import type { AccentTokens, ThemeTokens } from '@nayto/theme';
import type { Lang } from '@nayto/i18n';
import { apiFetch, getFirebaseAuth, refreshMe, useAuthSession } from '@/auth';
import { SIGN_IN_HELP_LINE_KEY, formatSignInError } from '@/auth/sign-in-errors';
import { Icon, PlatformBadge } from '@/components/icons';
import { useEnabledOnboardingSteps } from '@/services-context';
import type { OnboardingStep } from '@/services';

type Step = OnboardingStep;

type SocialPlatform = 'facebook' | 'twitter' | 'instagram' | 'tiktok';

type ClaimOutcomeKind =
  | 'conflict'
  | 'pending_approval'
  | 'source_created'
  | 'claimed'
  | 'already_has_handle'
  | 'error';

interface ClaimOutcome {
  outcome: ClaimOutcomeKind;
  message: string | null;
}

interface OnboardingProps {
  theme: ThemeTokens;
  accent: AccentTokens;
  lang: Lang;
  /** Kept for AppShell compatibility; the wizard no longer picks a
   * language (Settings does). */
  setLang?: (lang: Lang) => void;
  t: (key: string) => string;
  onFinish: () => void;
}

export function Onboarding({ theme, accent, lang, t, onFinish }: OnboardingProps) {
  const rtl = lang === 'he';
  // Live registry read: which steps this deployment runs. Was a
  // module-level constant before F-01.
  const ONBOARDING_STEPS = useEnabledOnboardingSteps();
  const [step, setStep] = useState<Step>(ONBOARDING_STEPS[0] ?? 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [socials, setSocials] = useState<Record<SocialPlatform, string>>({
    facebook: '',
    twitter: '',
    instagram: '',
    tiktok: '',
  });
  const [reports, setReports] = useState<Record<string, string>>({
    listening: 'daily',
    competitors: 'daily',
    leaderboard: 'weekly',
  });
  const [authError, setAuthError] = useState('');
  const [authErrorDetail, setAuthErrorDetail] = useState('');
  const [busy, setBusy] = useState(false);
  // Per-platform outcome of the last claim attempt — drives the inline
  // badge + helper text in the socials step.
  const [claimOutcomes, setClaimOutcomes] = useState<Partial<Record<SocialPlatform, ClaimOutcome>>>(
    {},
  );

  // The server-side account is created by /api/auth/session, which the
  // auth module fires from onAuthStateChanged right after Firebase sign-in.
  // We hand off only once that round-trip is done.
  const session = useAuthSession();
  const sessionReady = session.status === 'ready';
  // Firebase sign-in succeeded in this wizard; waiting for / acting on
  // the server session.
  const [signedIn, setSignedIn] = useState(false);
  // Not ready AND Firebase has no current user → the session sync failed
  // hard (e.g. 401 → signOut). Distinct from "still in flight", where
  // Firebase does hold a user.
  const sessionLost = signedIn && !sessionReady && getFirebaseAuth().currentUser == null;

  const next = () => {
    const idx = ONBOARDING_STEPS.indexOf(step);
    if (idx < ONBOARDING_STEPS.length - 1) setStep(ONBOARDING_STEPS[idx + 1]!);
    else onFinish();
  };

  // ─── E-mail + password sign-in ───────────────────────────────────

  const signIn = async () => {
    setAuthError('');
    setAuthErrorDetail('');
    const mail = email.trim();
    if (!mail) {
      setAuthError(t('obEmailRequired'));
      return;
    }
    if (!password) {
      setAuthError(t('obPasswordRequired'));
      return;
    }
    setBusy(true);
    try {
      await signInWithEmailAndPassword(getFirebaseAuth(), mail, password);
      // Firebase is signed in; onAuthStateChanged → POST /api/auth/session
      // runs in the auth module. The effect below takes over once the
      // session is ready.
      setPassword('');
      setSignedIn(true);
    } catch (err) {
      console.error('signIn failed', err);
      const formatted = formatSignInError(err, t);
      setAuthError(formatted.message);
      setAuthErrorDetail(formatted.detail);
    } finally {
      setBusy(false);
    }
  };

  // Hand-off after the server session exists: pull the fresh /api/me so
  // AppShell's role gate sees the server's answer. A freshly created
  // account is `pending`: finish the wizard now and let AppShell show the
  // PendingApprovalScreen. Approved roles continue to the next enabled
  // step (today: none → onFinish → default route).
  useEffect(() => {
    if (!signedIn || !sessionReady || step !== 'signin') return undefined;
    let cancelled = false;
    (async () => {
      const fresh = await refreshMe();
      if (cancelled) return;
      if (fresh?.role === 'pending') {
        onFinish();
        return;
      }
      next();
    })();
    return () => {
      cancelled = true;
    };
    // `next`/`onFinish` are stable enough for this one-shot hand-off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, sessionReady, step]);

  // ─── Style tokens ────────────────────────────────────────────────

  const labelStyle = {
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: 0.04,
    textTransform: 'uppercase' as const,
    color: theme.textSubtle,
  };
  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${theme.border}`,
    background: theme.surface,
    color: theme.text,
    fontSize: 16,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };
  const primaryBtn = {
    width: '100%',
    padding: '16px',
    borderRadius: 14,
    background: theme.chrome,
    color: theme.chromeText,
    border: 'none',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
  const secondaryBtn = {
    padding: '10px 16px',
    borderRadius: 10,
    background: 'transparent',
    color: theme.textMuted,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  // Progress indicator (one dot per enabled step; the active one is
  // wider). With a single step there is nothing to indicate.
  const progress =
    ONBOARDING_STEPS.length > 1 ? (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 32 }}>
        {ONBOARDING_STEPS.map((s, i) => {
          const idx = ONBOARDING_STEPS.indexOf(step);
          return (
            <div
              key={s}
              style={{
                width: i === idx ? 24 : 6,
                height: 6,
                borderRadius: 3,
                background: i <= idx ? accent.main : theme.border,
                transition: 'all 0.3s ease',
              }}
            />
          );
        })}
      </div>
    ) : null;

  const logo = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 32,
      }}
    >
      <img src="/logo.svg" width={56} height={56} alt="Strong Armenia" style={{ display: 'block' }} />
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: theme.text,
          marginTop: 16,
          letterSpacing: -0.3,
        }}
      >
        {t('welcome')}
      </div>
      <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4, textAlign: 'center' }}>
        {t('welcomeSub')}
      </div>
    </div>
  );

  const errorBox = authError ? (
    <div
      role="alert"
      style={{
        fontSize: 13,
        color: '#dc2626',
        background: '#fef2f2',
        padding: '10px 12px',
        borderRadius: 10,
        marginBottom: 12,
        border: '1px solid #fecaca',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        lineHeight: 1.45,
      }}
    >
      {authError}
      {authErrorDetail && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px solid #fecaca',
            fontSize: 11,
            color: '#7f1d1d',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            opacity: 0.85,
            direction: 'ltr',
            textAlign: 'start',
          }}
        >
          {authErrorDetail}
        </div>
      )}
    </div>
  ) : null;

  // ─── Per-step body ───────────────────────────────────────────────

  let body: React.ReactNode;
  if (step === 'signin') {
    const waiting = signedIn && !sessionLost;
    body = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && !waiting) void signIn();
        }}
      >
        {logo}
        {progress}
        <div style={{ ...labelStyle, marginBottom: 8 }}>{t('obEmail')}</div>
        <input
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          dir="ltr"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
          disabled={busy || waiting}
          style={{ ...inputStyle, marginBottom: 12 }}
        />
        <div style={{ ...labelStyle, marginBottom: 8 }}>{t('obPassword')}</div>
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          dir="ltr"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          disabled={busy || waiting}
          style={{ ...inputStyle, marginBottom: 16 }}
        />
        {errorBox}
        {sessionLost && (
          <div
            role="alert"
            style={{
              fontSize: 13,
              color: '#dc2626',
              background: '#fef2f2',
              padding: '10px 12px',
              borderRadius: 10,
              marginBottom: 12,
              border: '1px solid #fecaca',
              lineHeight: 1.45,
            }}
          >
            {t('obServerRejected')}
          </div>
        )}
        <button
          type="submit"
          style={primaryBtn}
          disabled={busy || waiting || !email.trim() || !password}
        >
          {busy
            ? t('obSigningIn')
            : waiting
              ? t('obPreparingAccount')
              : t('obSignIn')}
        </button>
        {sessionLost && (
          <button
            type="button"
            style={{ ...secondaryBtn, width: '100%', marginTop: 12 }}
            onClick={() => {
              setSignedIn(false);
              setAuthError('');
              setAuthErrorDetail('');
            }}
          >
            {t('obTryAgain')}
          </button>
        )}
        <div
          style={{
            fontSize: 12,
            color: theme.textSubtle,
            marginTop: 16,
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          {t(SIGN_IN_HELP_LINE_KEY)}
        </div>
      </form>
    );
  } else if (step === 'socials') {
    const socialList: Array<{ k: SocialPlatform; label: string; placeholder: string }> = [
      { k: 'facebook', label: 'Facebook', placeholder: 'fb.com/your-page' },
      { k: 'twitter', label: 'Twitter / X', placeholder: 'x.com/@your_handle' },
      { k: 'instagram', label: 'Instagram', placeholder: '@your_handle' },
      { k: 'tiktok', label: 'TikTok', placeholder: '@your_handle' },
    ];
    // Per-platform claim outcomes:
    //   conflict          — red, blocks (admin must resolve)
    //   pending_approval  — amber (admin will verify)
    //   source_created    — green (new source auto-created)
    //   claimed           — green (matched existing source)
    //   error             — red (generic failure, can retry)
    // When any claim succeeded we advance; conflicts stay on this screen
    // so the user understands why a specific platform didn't link.
    const outcomeColors: Record<ClaimOutcomeKind, string> = {
      conflict: '#dc2626',
      pending_approval: '#d97706',
      source_created: '#16a34a',
      claimed: '#16a34a',
      already_has_handle: '#64748b',
      error: '#dc2626',
    };
    const outcomeLabels: Record<ClaimOutcomeKind, string> = {
      conflict: 'CONFLICT',
      pending_approval: 'PENDING',
      source_created: 'NEW',
      claimed: 'LINKED',
      already_has_handle: 'EXISTS',
      error: 'ERROR',
    };

    body = (
      <div>
        {logo}
        {progress}
        <div style={{ ...labelStyle, marginBottom: 8 }}>{t('connectSocials')}</div>
        <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
          {t('connectHint')}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {socialList.map((s) => {
            const has = socials[s.k];
            const outcome = claimOutcomes[s.k];
            const borderColor = outcome
              ? outcomeColors[outcome.outcome]
              : has
                ? accent.main
                : theme.border;
            return (
              <div key={s.k}>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderRadius: 12,
                    border: `1px solid ${borderColor}`,
                    background: theme.surface,
                  }}
                >
                  <PlatformBadge platform={s.k} size={28} />
                  <input
                    value={socials[s.k]}
                    onChange={(e) => setSocials({ ...socials, [s.k]: e.target.value })}
                    placeholder={s.placeholder}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      color: theme.text,
                      fontSize: 14,
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                  {has && !outcome && <Icon name="check" size={18} color={accent.main} />}
                  {outcome && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10,
                        background: outcomeColors[outcome.outcome],
                        color: '#fff',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        letterSpacing: 0.3,
                      }}
                    >
                      {outcomeLabels[outcome.outcome]}
                    </span>
                  )}
                </div>
                {outcome?.message && (
                  <div
                    style={{
                      fontSize: 11,
                      color: outcomeColors[outcome.outcome],
                      marginTop: 4,
                      marginInlineStart: 46,
                    }}
                  >
                    {outcome.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: theme.textSubtle, marginBottom: 12 }}>
          You can skip this step — accounts can be linked anytime from Settings.
        </div>
        {errorBox}
        <button
          style={primaryBtn}
          disabled={busy}
          onClick={async () => {
            // Try each claim; collect per-platform outcomes. If any conflict
            // appears we do NOT advance — the user sees the badges inline
            // and can decide whether to clear/fix the input or proceed.
            setBusy(true);
            setAuthError('');
            const newOutcomes: Partial<Record<SocialPlatform, ClaimOutcome>> = {};
            let anyConflict = false;
            try {
              for (const s of socialList) {
                const raw = (socials[s.k] || '').trim();
                if (!raw) continue;
                try {
                  const resp = await apiFetch('/api/me/handles', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'add', platform: s.k, sourceId: raw }),
                  });
                  const body = (await resp.json().catch(() => ({}))) as {
                    code?: string;
                    error?: string;
                    outcome?: ClaimOutcomeKind;
                    monitoredSource?: { displayName?: string; sourceId?: string };
                  };
                  if (resp.status === 409 || body.code === 'HANDLE_CONFLICT') {
                    newOutcomes[s.k] = {
                      outcome: 'conflict',
                      message:
                        'This account is already claimed by another user. Contact admin if this is a mistake.',
                    };
                    anyConflict = true;
                  } else if (!resp.ok) {
                    newOutcomes[s.k] = {
                      outcome: 'error',
                      message: body.error ?? `HTTP ${resp.status}`,
                    };
                    anyConflict = true;
                  } else {
                    const outcome: ClaimOutcomeKind = body.outcome ?? 'claimed';
                    const srcName =
                      body.monitoredSource?.displayName ?? body.monitoredSource?.sourceId;
                    const msgByOutcome: Partial<Record<ClaimOutcomeKind, string>> = {
                      pending_approval: `Linked to "${srcName}" — waiting for admin approval`,
                      source_created: 'New source registered. First scrape runs on the next cycle.',
                      claimed: srcName ? `Linked to "${srcName}".` : 'Linked successfully.',
                      already_has_handle: 'You already have a handle on this platform.',
                    };
                    newOutcomes[s.k] = {
                      outcome,
                      message: msgByOutcome[outcome] ?? null,
                    };
                  }
                } catch (err) {
                  newOutcomes[s.k] = {
                    outcome: 'error',
                    message: err instanceof Error ? err.message : String(err),
                  };
                  anyConflict = true;
                }
              }
              setClaimOutcomes(newOutcomes);
            } finally {
              setBusy(false);
            }
            if (anyConflict) return;
            // Pull the fresh /api/me so AppShell's role gate can pick it
            // up; a pending user skips the rest of the wizard and AppShell
            // hands off to the PendingApprovalScreen.
            const fresh = await refreshMe();
            if (fresh?.role === 'pending') {
              onFinish();
              return;
            }
            next();
          }}
        >
          {busy ? 'Linking…' : t('continue')}
        </button>
      </div>
    );
  } else if (step === 'reports') {
    const items: Array<{ k: string; label: string }> = [
      { k: 'listening', label: t('socialListening') },
      { k: 'competitors', label: t('liveNews') },
      { k: 'leaderboard', label: t('leaderboards') },
    ];
    const freq = [
      { k: 'hourly', l: 'Hourly' },
      { k: 'daily', l: 'Daily' },
      { k: 'weekly', l: 'Weekly' },
      { k: 'off', l: 'Off' },
    ];
    body = (
      <div>
        {logo}
        {progress}
        <div style={{ ...labelStyle, marginBottom: 8 }}>{t('chooseReports')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {items.map((it) => (
            <div
              key={it.k}
              style={{
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${theme.border}`,
                background: theme.surface,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.text,
                  marginBottom: 8,
                }}
              >
                {it.label}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {freq.map((f) => (
                  <button
                    key={f.k}
                    onClick={() => setReports({ ...reports, [it.k]: f.k })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      borderRadius: 8,
                      border: `1px solid ${reports[it.k] === f.k ? accent.main : theme.border}`,
                      background: reports[it.k] === f.k ? accent.soft : 'transparent',
                      color: reports[it.k] === f.k ? accent.on : theme.textMuted,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {f.l}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button style={primaryBtn} onClick={onFinish}>
          {t('finish')}
        </button>
      </div>
    );
  }

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        minHeight: '100%',
        background: theme.bg,
        overflow: 'auto',
        padding: '60px 24px 24px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 460, margin: '0 auto' }}>{body}</div>
    </div>
  );
}
